import React, { FC } from "react"
import L10n from "lib/carrot-l10n"
import { PageHeader, ResponsiveWrapper } from "../molecules/Molecules"
import { ButtonContents, TalkToCarrotButton } from "../../talk-to-carrot/TalkToCarrotButton"
import { BodyLayout } from "../atoms/layouts/BodyLayout"
import { Spacer } from "../atoms/Atoms"
import { Button } from "../atoms/buttons/Button"
import { ButtonPrimary } from "../atoms/buttons/ButtonPrimary"
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { Body } from "../atoms/Bodies"
import { useSelector } from "react-redux"
import { getHasAnUpcomingOrCompletedExpertChat, getHasMetSignatureRequirement } from "reducers/userInfo"
import { useToggle } from "utils/Hooks"
import { Modal } from "../modal/Modal"
import Settings from "../../../utils/CarrotConfig"
import styled from "styled-components"

const l = new L10n({ locale: "en-US" }).getBoundLocalize()

type BackButtonsProps = {
  backOnClick: () => void
  doThisLaterOnClick: () => void
}

const BackButtons: FC<BackButtonsProps> = ({ backOnClick, doThisLaterOnClick }: BackButtonsProps) => {
  return (
    <div className="flex flex-row justify-end">
      <div onClick={backOnClick} className="mr3">
        <Button outline className="min-w3 tc">
          {l`Back`}
        </Button>
      </div>
      <ButtonPrimary className="min-w3 tc" onClick={doThisLaterOnClick}>
        {l`Do this later`}
      </ButtonPrimary>
    </div>
  )
}

type DocusignModalProps = {
  onClose: () => void
}

const DocusignModal: FC<DocusignModalProps> = ({ onClose }) => {
  return (
    <Modal titleText={l`Authorization Form Modal`} onExit={onClose} fullscreen={true}>
      <div className="tr ph35-s pb35-s ph45-ns pb45-ns w-100 h-100">
        <iframe
          className="w-100 h-100 border-box ba b--black-30"
          title={l`Authorization Form`}
          src={`/signature`}
          frameBorder="0"
          allowFullScreen
        />
      </div>
    </Modal>
  )
}

type SignAuthorizationFormButtonProps = {
  iconName?: string
  label: string
  onClick: () => void
  enabled?: boolean
}

const SignAuthorizationFormButton: FC<SignAuthorizationFormButtonProps> = ({
  iconName,
  label,
  onClick,
  enabled = true
}: SignAuthorizationFormButtonProps) => {
  const cursorType = enabled ? "pointer" : "not-allowed"
  const buttonOpacity = enabled ? "" : "o-30"
  const onClickHandler = enabled ? onClick : null

  return (
    <div className={buttonOpacity}>
      <a onClick={onClickHandler} className={`no-underline ${cursorType}`}>
        <ButtonContents label={label} iconName={iconName} enabled={enabled} />
      </a>
    </div>
  )
}

type SignAuthorizationFormModalButtonProps = {
  enabled: boolean
}

const SignAuthorizationFormModalButton: FC<SignAuthorizationFormModalButtonProps> = ({ enabled }) => {
  const [isModalOpen, toggleIsModalOpen] = useToggle(false)

  return (
    <>
      <SignAuthorizationFormButton
        enabled={enabled}
        iconName="icn-documents"
        label={l`Sign your document`}
        onClick={() => {
          toggleIsModalOpen()
        }}
      />
      {isModalOpen && <DocusignModal onClose={toggleIsModalOpen} />}
    </>
  )
}

type IvfRequirementsProps = {
  backOnClick: () => void
  doThisLaterOnClick: () => void
}

const RequirementsButtons = () => {
  const hasAnUpcomingOrCompletedExpertChat = useSelector(getHasAnUpcomingOrCompletedExpertChat)
  const hasMetSignatureRequirement = useSelector(getHasMetSignatureRequirement)
  return (
    <>
      <TalkToCarrotButton
        enabled={!hasAnUpcomingOrCompletedExpertChat}
        useModal={true}
        to={Settings.IVF_TREATMENT_OPTIONS_URL}
        iconName="icn-schedule-chat"
        label={l`Schedule your expert chat`}
        chatType="IvfRequirementsChat"
      />
      <SignAuthorizationFormModalButton enabled={!hasMetSignatureRequirement} />
    </>
  )
}

const StyledP = styled.p`
  font-weight: 500;
  margin-bottom: 4px;
  line-height: 150%;
`

const RequirementsSteps = () => (
  <ResponsiveWrapper padding={0.75}>
    <div>
      <StyledP>Step 1</StyledP>
      <Body color={"black-80"}>
        {l`Meet with an IVF expert to discuss treatment options and what Carrot can cover, and complete a questionnaire before and after. If you’re pursuing IVF with a partner, both of you need to attend.`}
      </Body>
    </div>
    <div>
      <StyledP>Step 2</StyledP>
      <Body color={"black-80"}>
        {l`Allow Carrot to view relevant health information for the person(s) receiving an embryo transfer. Personal information is always kept secure, and we will never share it with your employer or any other third party.`}
      </Body>
    </div>
    <RequirementsButtons />
  </ResponsiveWrapper>
)

const IvfRequirements: FC<IvfRequirementsProps> = ({ backOnClick, doThisLaterOnClick }: IvfRequirementsProps) => (
  <MasterLayout header={<PageHeader title={l`How to get reimbursed for IVF`} icon="icn-get-reimbursed" />}>
    <BodyLayout>
      <Body color={"black-80"}>
        {l`To support healthy outcomes, members are required to complete two steps before they can be reimbursed for IVF-related expenses.`}
      </Body>
      <Spacer height={1.5} />
      <Body color={"black-80"}>
        {l`If you decide to pursue IVF, we recommend completing these as early as possible to ensure that we'll be able to reimburse you.`}
      </Body>
      <Spacer height={2.5} />
      <RequirementsSteps />
      <Spacer height={3} />
      <BackButtons backOnClick={backOnClick} doThisLaterOnClick={doThisLaterOnClick} />
      <Spacer height={1.5} />
      <div className="bb b--black-10" />
    </BodyLayout>
  </MasterLayout>
)

export { IvfRequirements }
